/**
 * In-memory history of network states.
 *
 * Snapshots are the lightweight unit (topology + weights as plain numbers) used
 * for the timeline and for D3 rendering. Checkpoints are a separate, explicit
 * concern handled by `checkpoints.ts`.
 */
import type { EpochMetrics, NetworkSnapshot, StepMetrics } from '../engine/types.ts';

export interface HistoryEntry {
  snapshot: NetworkSnapshot;
  /** Cumulative step count at the time of capture. */
  step: number;
  /** Set when the entry was created by a growth event rather than a timer. */
  reason: 'initial' | 'interval' | 'growth';
  label: string;
}

export interface MetricPoint {
  step: number;
  /** 1-based epoch the batch belongs to; the chart groups by it for its epoch view. */
  epoch: number;
  loss: number;
  accuracy: number;
  precision: number;
  recall: number;
  f1: number;
  /** True when a growth event happened at this step. */
  growth: boolean;
}

export class SnapshotStore {
  #entries: HistoryEntry[] = [];
  #metrics: MetricPoint[] = [];
  #epochs: EpochMetrics[] = [];

  get entries(): readonly HistoryEntry[] {
    return this.#entries;
  }

  get metrics(): readonly MetricPoint[] {
    return this.#metrics;
  }

  get epochs(): readonly EpochMetrics[] {
    return this.#epochs;
  }

  addSnapshot(entry: HistoryEntry): HistoryEntry {
    this.#entries.push(entry);
    return entry;
  }

  addStep(metrics: StepMetrics): MetricPoint {
    const point: MetricPoint = {
      step: metrics.step,
      epoch: metrics.epoch,
      loss: metrics.loss,
      accuracy: metrics.accuracy,
      precision: metrics.precision,
      recall: metrics.recall,
      f1: metrics.f1,
      growth: false,
    };
    this.#metrics.push(point);
    return point;
  }

  /** Marks the most recent metric point as a growth event. */
  markGrowth(): void {
    const last = this.#metrics.at(-1);
    if (last) {
      last.growth = true;
    }
  }

  addEpoch(metrics: EpochMetrics): void {
    this.#epochs.push(metrics);
  }

  clear(): void {
    this.#entries = [];
    this.#metrics = [];
    this.#epochs = [];
  }
}
